import { Caption1, makeStyles, mergeClasses, tokens, Tooltip } from "@fluentui/react-components";
import {
  Checkmark12Filled,
  Folder20Regular,
  PeopleTeam20Regular,
  PersonAdd20Regular,
  Warning16Regular,
} from "@fluentui/react-icons";
import { AppDialog } from "@/components/app-dialog";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { useWorkspaceDanger } from "@/features/access/workspace-danger";
import {
  acceptIncomingInvite,
  declineIncomingInvite,
  dismissIncomingInvite,
  incomingInviteTitle,
  incomingJoinedSentence,
  INVITE_MISSING,
  useMockPlanAccess,
  type IncomingDecision,
  type IncomingInvite,
  type IncomingInviteScope,
} from "@/features/access/mock-plan-access";
import { useCatalogStore } from "@/store/catalog-store";
import { inviteTargetMissing } from "./inbox-model";

function acceptInvite(invite: IncomingInvite) {
  acceptIncomingInvite(invite.id);
  if (invite.scope !== "workspace") return;
  useCatalogStore.getState().joinWorkspaceFromInvite({ name: invite.workspaceName, roleLabel: invite.role });
}

const SCOPE_LABEL: Record<IncomingInviteScope, string> = {
  workspace: "워크스페이스",
  plan: "플랜",
  guest: "게스트",
};

function formatInviteContext(invite: IncomingInvite): string {
  if (invite.scope === "workspace") return `${invite.role} 권한으로 초대됨`;
  if (invite.scope === "guest") return `${invite.workspaceName} · 이 플랜만 열람 · ${invite.role} 권한`;
  if (invite.alreadyReading) return `${invite.workspaceName} · 이미 열람 중 · ${invite.role} 권한`;
  return `${invite.workspaceName} · ${invite.role} 권한`;
}

function ScopeIcon({ scope, missing = false }: { scope: IncomingInviteScope; missing?: boolean }) {
  if (missing) return <Warning16Regular />;
  if (scope === "workspace") return <PeopleTeam20Regular />;
  if (scope === "guest") return <PersonAdd20Regular />;
  return <Folder20Regular />;
}

const useStyles = makeStyles({
  list: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
    minWidth: 0,
  },
  bare: {
    border: "none",
    borderRadius: 0,
    backgroundColor: "transparent",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
    padding: "10px 12px",
  },
  rowLine: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  iconBox: {
    width: "32px",
    height: "32px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  iconBoxMuted: {
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground3,
  },
  text: {
    minWidth: 0,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  name: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    minWidth: 0,
  },
  nameMuted: {
    color: tokens.colorNeutralForeground2,
  },
  scopeTag: {
    display: "inline-flex",
    alignItems: "center",
    height: "18px",
    paddingLeft: "6px",
    paddingRight: "6px",
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  meta: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  nowrap: {
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  outcomePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    height: "22px",
    paddingLeft: "8px",
    paddingRight: "8px",
    borderRadius: tokens.borderRadiusCircular,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  outcomeAccepted: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  outcomeDeclined: {
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground3,
  },
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalXS,
    paddingLeft: "2px",
    paddingRight: "2px",
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  sectionCount: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
});

export function InboxInviteRows({
  invites,
  planTrashNames,
  workspaceTrashNames,
  bare = false,
}: {
  invites: IncomingInvite[];
  planTrashNames: readonly string[];
  workspaceTrashNames: readonly string[];
  bare?: boolean;
}) {
  const styles = useStyles();
  return (
    <div className={mergeClasses(styles.list, bare && styles.bare)}>
      {invites.map((invite, index) => {
        const title = incomingInviteTitle(invite);
        const missing = inviteTargetMissing(invite, planTrashNames, workspaceTrashNames);
        const meta = missing ? INVITE_MISSING : formatInviteContext(invite);
        return (
          <div key={invite.id} className={mergeClasses(styles.row, index > 0 && styles.rowLine)}>
            <span className={mergeClasses(styles.iconBox, missing && styles.iconBoxMuted)} aria-hidden>
              <ScopeIcon scope={invite.scope} missing={missing} />
            </span>
            <div className={styles.text}>
              <div className={styles.titleRow}>
                <Tooltip content={title} relationship="description">
                  <span className={mergeClasses(styles.name, missing && styles.nameMuted)}>{title}</span>
                </Tooltip>
                <span className={styles.scopeTag}>{SCOPE_LABEL[invite.scope]}</span>
              </div>
              <Tooltip content={meta} relationship="description">
                <Caption1 className={styles.meta}>{meta}</Caption1>
              </Tooltip>
            </div>
            <div className={styles.actions}>
              {missing ? (
                <FuiButton className={styles.nowrap} appearance="subtle" density="dialog" onClick={() => dismissIncomingInvite(invite.id)}>
                  지우기
                </FuiButton>
              ) : (
                <>
                  <FuiButton className={styles.nowrap} appearance="secondary" density="dialog" onClick={() => declineIncomingInvite(invite.id)}>
                    거절
                  </FuiButton>
                  <FuiButton className={styles.nowrap} appearance="primary" density="dialog" onClick={() => acceptInvite(invite)}>
                    수락
                  </FuiButton>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function InboxDecisionRows({ decisions }: { decisions: IncomingDecision[] }) {
  const styles = useStyles();
  return (
    <div className={styles.list}>
      {decisions.map((decision, index) => {
        const title = incomingInviteTitle(decision.invite);
        const accepted = decision.outcome === "수락";
        const context = formatInviteContext(decision.invite);
        const detail = accepted ? incomingJoinedSentence(decision.invite) : `${title} 초대를 거절했습니다`;
        return (
          <div key={decision.id} className={mergeClasses(styles.row, index > 0 && styles.rowLine)} title={detail}>
            <span className={mergeClasses(styles.iconBox, !accepted && styles.iconBoxMuted)} aria-hidden>
              <ScopeIcon scope={decision.invite.scope} />
            </span>
            <div className={styles.text}>
              <div className={styles.titleRow}>
                <Tooltip content={title} relationship="description">
                  <span className={mergeClasses(styles.name, !accepted && styles.nameMuted)}>{title}</span>
                </Tooltip>
                <span className={styles.scopeTag}>{SCOPE_LABEL[decision.invite.scope]}</span>
              </div>
              <Tooltip content={detail} relationship="description">
                <Caption1 className={styles.meta}>{context}</Caption1>
              </Tooltip>
            </div>
            <span className={mergeClasses(styles.outcomePill, accepted ? styles.outcomeAccepted : styles.outcomeDeclined)}>
              {accepted ? <Checkmark12Filled /> : null}
              {accepted ? "참여됨" : "거절됨"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** 계정 단위로 받은 초대. 워크스페이스에 아직 없는 초대도 여기서 수락한다. */
export function ReceivedInvitesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const styles = useStyles();
  const access = useMockPlanAccess();
  const danger = useWorkspaceDanger();
  const planTrashNames = access.trash.map((item) => item.name);
  const workspaceTrashNames = danger.trash.map((item) => item.name);
  const pending = access.incoming;
  const past = access.incomingDecisions;

  return (
    <AppDialog open={open} onClose={onClose} title="받은 초대" width="md">
      <div className={styles.stack}>
        {pending.length > 0 ? (
          <div className={styles.group}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionTitle}>대기 중인 초대</span>
              <span className={styles.sectionCount}>{pending.length}건</span>
            </div>
            <InboxInviteRows invites={pending} planTrashNames={planTrashNames} workspaceTrashNames={workspaceTrashNames} />
          </div>
        ) : past.length === 0 ? (
          <GhostEmptyState compact title="받은 초대가 없습니다" description="초대가 오면 여기에 모입니다." />
        ) : null}
        {past.length > 0 ? (
          <div className={styles.group}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionTitle}>지난 결정</span>
              <span className={styles.sectionCount}>{past.length}건</span>
            </div>
            <InboxDecisionRows decisions={past} />
          </div>
        ) : null}
      </div>
    </AppDialog>
  );
}
